import { useState, useEffect, useCallback, useMemo } from 'react'
import { useAuth } from '@/lib/auth'
import { supabase } from '@/lib/supabase'
import {
  detectUserTimezone,
  formatDateInTimezone,
  getCurrentTimeInTimezone,
  convertUTCToTimezone,
  getFormattedDateWithTimezone,
  getTimezoneDisplayName,
  getTimezoneAbbreviation,
  isDSTActive,
  isValidTimezone,
  DEFAULT_TIMEZONE,
  DEFAULT_FORMAT,
  FormattedDate,
  getRelativeTime,
  getSmartRelativeTime,
  getContextualTime,
  getTimeUntil,
  type RelativeTimeOptions,
  type RelativeTimeResult
} from '@/lib/timezone-utils'

/**
 * Hook for managing timezone functionality throughout the application
 * Provides user timezone management, date formatting, and timezone operations
 */

interface UseTimezoneOptions {
  /** Whether to auto-detect timezone on first load */
  autoDetect?: boolean
  /** Default timezone if user hasn't set one */
  defaultTimezone?: string
  /** Whether to update user profile when timezone is detected/changed */
  updateProfile?: boolean
}

interface UseTimezoneReturn {
  /** Current user's timezone */
  timezone: string
  /** Whether timezone is being loaded/detected */
  isLoading: boolean
  /** Whether auto-detection is in progress */
  isAutoDetecting: boolean
  /** Error if any occurred */
  error: string | null
  
  // Core functionality
  /** Set user's timezone (optionally updates profile) */
  setTimezone: (timezone: string) => Promise<void>
  /** Auto-detect and set user's timezone */
  detectAndSetTimezone: () => Promise<void>
  /** Reset timezone to default */
  resetTimezone: () => Promise<void>
  
  // Date formatting utilities
  /** Format a date in user's timezone */
  formatDate: (date: Date | string, format?: string) => string
  /** Format only time in user's timezone */
  formatTime: (date: Date | string, use24Hour?: boolean) => string
  /** Get formatted date with full timezone info */
  formatDateWithTimezone: (date: Date | string, format?: string) => FormattedDate
  /** Get current time in user's timezone */
  getCurrentTime: () => Date
  /** Convert UTC date to user's timezone */
  fromUTC: (date: Date | string) => Date
  
  // Timezone information
  /** Get display name for current timezone */
  getDisplayName: () => string
  /** Get timezone abbreviation for current date */
  getAbbreviation: (date?: Date) => string
  /** Check if DST is currently active */
  isDSTActive: (date?: Date) => boolean
  /** Validate if a timezone string is valid */
  isValidTimezone: (tz: string) => boolean
  
  // Relative time utilities
  /** Get relative time with full options and result details */
  getRelativeTime: (date: Date | string, options?: RelativeTimeOptions) => RelativeTimeResult
  /** Get smart relative time that switches to absolute after threshold */
  getSmartRelativeTime: (date: Date | string, options?: Omit<RelativeTimeOptions, 'absoluteThreshold'> & { smartThreshold?: number }) => string
  /** Get contextual time with intelligent formatting */
  getContextualTime: (date: Date | string, context?: 'compact' | 'normal' | 'detailed') => string
  /** Get time until/since target date with precise calculations */
  getTimeUntil: (targetDate: Date | string, options?: { includeSeconds?: boolean; precision?: 'low' | 'medium' | 'high' }) => { timeString: string; isPast: boolean; totalSeconds: number }
}

export function useTimezone(options: UseTimezoneOptions = {}): UseTimezoneReturn {
  const {
    autoDetect = true,
    defaultTimezone = DEFAULT_TIMEZONE,
    updateProfile = true
  } = options

  const { user, updateProfile: updateUserProfile } = useAuth()
  
  const [timezone, setTimezoneState] = useState<string>(defaultTimezone)
  const [isLoading, setIsLoading] = useState(true)
  const [isAutoDetecting, setIsAutoDetecting] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Load user's timezone from profile
  useEffect(() => {
    async function loadUserTimezone() {
      setIsLoading(true)
      setError(null)
      
      try {
        if (user) {
          // Get timezone from user profile and auth metadata
          const userTimezone = user.user_metadata?.timezone || user.app_metadata?.timezone
          
          if (userTimezone && isValidTimezone(userTimezone)) {
            setTimezoneState(userTimezone)
          } else {
            // Check profile table for timezone
            const { data: profile } = await supabase
              .schema('marketing_and_customer_success')
              .from('profiles')
              .select('timezone')
              .eq('id', user.id)
              .single()

            if (profile?.timezone && isValidTimezone(profile.timezone)) {
              setTimezoneState(profile.timezone)
            } else if (autoDetect) {
              // Auto-detect if no valid timezone set anywhere
              await detectAndSetTimezone()
            } else {
              setTimezoneState(defaultTimezone)
            }
          }
        } else if (autoDetect) {
          // Detect timezone for non-authenticated users
          const detectedTz = detectUserTimezone()
          if (isValidTimezone(detectedTz)) {
            setTimezoneState(detectedTz)
          } else {
            setTimezoneState(defaultTimezone)
          }
        } else {
          setTimezoneState(defaultTimezone)
        }
      } catch (err) {
        console.error('Error loading user timezone:', err)
        setError('Failed to load timezone')
        setTimezoneState(defaultTimezone)
      } finally {
        setIsLoading(false)
      }
    }

    loadUserTimezone()
  }, [user, autoDetect, defaultTimezone])

  // Set timezone with optional profile update
  const setTimezone = useCallback(async (newTimezone: string) => {
    if (!isValidTimezone(newTimezone)) {
      const errorMsg = `Invalid timezone: ${newTimezone}`
      setError(errorMsg)
      throw new Error(errorMsg)
    }

    setError(null)
    setTimezoneState(newTimezone)

    // Update user profile if authenticated and updateProfile is enabled
    if (user && updateProfile && updateUserProfile) {
      try {
        await updateUserProfile({
          timezone: newTimezone
        })
      } catch (err) {
        console.error('Error updating timezone in profile:', err)
        setError('Failed to save timezone to profile')
        // Don't throw here as the local timezone was set successfully
      }
    }
  }, [user, updateProfile, updateUserProfile])

  // Auto-detect and set timezone
  const detectAndSetTimezone = useCallback(async () => {
    setIsAutoDetecting(true)
    setError(null)
    
    try {
      const detectedTz = detectUserTimezone()
      if (isValidTimezone(detectedTz)) {
        await setTimezone(detectedTz)
      } else {
        throw new Error('Could not detect a valid timezone')
      }
    } catch (err) {
      console.error('Error auto-detecting timezone:', err)
      setError('Failed to detect timezone')
    } finally {
      setIsAutoDetecting(false)
    }
  }, [setTimezone])

  // Reset to default timezone
  const resetTimezone = useCallback(async () => {
    await setTimezone(defaultTimezone)
  }, [setTimezone, defaultTimezone])

  // Date formatting utilities using current timezone
  const formatDate = useCallback((date: Date | string, format: string = DEFAULT_FORMAT): string => {
    return formatDateInTimezone(date, timezone, format)
  }, [timezone])

  const formatTime = useCallback((date: Date | string, use24Hour: boolean = false): string => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return dateObj.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: !use24Hour,
        hour: 'numeric',
        minute: '2-digit'
      })
    } catch (error) {
      console.error('Error formatting time:', error)
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return dateObj.toLocaleTimeString('en-US', {
        hour12: !use24Hour,
        hour: 'numeric',
        minute: '2-digit'
      })
    }
  }, [timezone])

  const formatDateWithTimezone = useCallback((date: Date | string, format: string = DEFAULT_FORMAT): FormattedDate => {
    return getFormattedDateWithTimezone(date, timezone, format)
  }, [timezone])

  const getCurrentTime = useCallback((): Date => {
    return getCurrentTimeInTimezone(timezone)
  }, [timezone])

  const fromUTC = useCallback((date: Date | string): Date => {
    return convertUTCToTimezone(date, timezone)
  }, [timezone])

  // Timezone information utilities
  const getDisplayName = useCallback((): string => {
    return getTimezoneDisplayName(timezone)
  }, [timezone])

  const getAbbreviation = useCallback((date?: Date): string => {
    return getTimezoneAbbreviation(timezone, date)
  }, [timezone])

  const isDSTActiveForTimezone = useCallback((date?: Date): boolean => {
    return isDSTActive(timezone, date)
  }, [timezone])

  // Relative time utilities using current timezone
  const getRelativeTimeInTimezone = useCallback((date: Date | string, options?: RelativeTimeOptions): RelativeTimeResult => {
    return getRelativeTime(date, timezone, options)
  }, [timezone])

  const getSmartRelativeTimeInTimezone = useCallback((
    date: Date | string, 
    options?: Omit<RelativeTimeOptions, 'absoluteThreshold'> & { smartThreshold?: number }
  ): string => {
    return getSmartRelativeTime(date, timezone, options)
  }, [timezone])

  const getContextualTimeInTimezone = useCallback((
    date: Date | string, 
    context?: 'compact' | 'normal' | 'detailed'
  ): string => {
    return getContextualTime(date, timezone, context)
  }, [timezone])

  const getTimeUntilInTimezone = useCallback((
    targetDate: Date | string,
    options?: { includeSeconds?: boolean; precision?: 'low' | 'medium' | 'high' }
  ) => {
    return getTimeUntil(targetDate, timezone, options)
  }, [timezone])

  // Memoized return object for performance
  const returnValue = useMemo(() => ({
    timezone,
    isLoading,
    isAutoDetecting,
    error,
    setTimezone,
    detectAndSetTimezone,
    resetTimezone,
    formatDate,
    formatTime,
    formatDateWithTimezone,
    getCurrentTime,
    fromUTC,
    getDisplayName,
    getAbbreviation,
    isDSTActive: isDSTActiveForTimezone,
    isValidTimezone,
    // Relative time utilities
    getRelativeTime: getRelativeTimeInTimezone,
    getSmartRelativeTime: getSmartRelativeTimeInTimezone,
    getContextualTime: getContextualTimeInTimezone,
    getTimeUntil: getTimeUntilInTimezone
  }), [
    timezone,
    isLoading,
    isAutoDetecting,
    error,
    setTimezone,
    detectAndSetTimezone,
    resetTimezone,
    formatDate,
    formatTime,
    formatDateWithTimezone,
    getCurrentTime,
    fromUTC,
    getDisplayName,
    getAbbreviation,
    isDSTActiveForTimezone,
    getRelativeTimeInTimezone,
    getSmartRelativeTimeInTimezone,
    getContextualTimeInTimezone,
    getTimeUntilInTimezone
  ])

  return returnValue
}

/**
 * Hook for timezone operations without user profile integration
 * Useful for components that need timezone functionality but don't need user-specific timezones
 */
export function useTimezoneOperations(timezone: string = DEFAULT_TIMEZONE) {
  const formatDate = useCallback((date: Date | string, format: string = DEFAULT_FORMAT): string => {
    return formatDateInTimezone(date, timezone, format)
  }, [timezone])

  const formatTime = useCallback((date: Date | string, use24Hour: boolean = false): string => {
    try {
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return dateObj.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour12: !use24Hour,
        hour: 'numeric',
        minute: '2-digit'
      })
    } catch (error) {
      console.error('Error formatting time:', error)
      const dateObj = typeof date === 'string' ? new Date(date) : date
      return dateObj.toLocaleTimeString('en-US', {
        hour12: !use24Hour,
        hour: 'numeric',
        minute: '2-digit'
      })
    }
  }, [timezone])

  const getCurrentTime = useCallback((): Date => {
    return getCurrentTimeInTimezone(timezone)
  }, [timezone])

  const fromUTC = useCallback((date: Date | string): Date => {
    return convertUTCToTimezone(date, timezone)
  }, [timezone])

  const getDisplayName = useCallback((): string => {
    return getTimezoneDisplayName(timezone)
  }, [timezone])

  const getAbbreviation = useCallback((date?: Date): string => {
    return getTimezoneAbbreviation(timezone, date)
  }, [timezone])

  const isDSTActiveForTimezone = useCallback((date?: Date): boolean => {
    return isDSTActive(timezone, date)
  }, [timezone])

  return useMemo(() => ({
    timezone,
    formatDate,
    formatTime,
    getCurrentTime,
    fromUTC,
    getDisplayName,
    getAbbreviation,
    isDSTActive: isDSTActiveForTimezone,
    isValidTimezone
  }), [
    timezone,
    formatDate,
    formatTime,
    getCurrentTime,
    fromUTC,
    getDisplayName,
    getAbbreviation,
    isDSTActiveForTimezone
  ])
}

/**
 * Simple hook to get the current user's timezone (read-only)
 */
export function useUserTimezone(): string {
  const { user } = useAuth()
  
  return useMemo(() => {
    if (!user) {
      return detectUserTimezone()
    }
    
    const userTimezone = user.user_metadata?.timezone || user.app_metadata?.timezone
    return (userTimezone && isValidTimezone(userTimezone)) ? userTimezone : detectUserTimezone()
  }, [user])
}

export default useTimezone